import React, { FunctionComponent, useState } from 'react';
import { useLocation } from 'react-router';
import { useDispatch } from 'react-redux';
import { Grid, Button, Colors, AnalyticsComponent } from '@inplayer-org/inplayer-ui';
import { Field, FieldArray, Formik, FormikProps, FormikHelpers } from 'formik';
import { array, object, string } from 'yup';
import { FaPlus } from 'react-icons/fa';
import { IoMdTrash } from 'react-icons/io';

// hooks
import useRestrictionRules from 'restrictions/roles/hooks/useRestrictionRules';

// types
import { MetadataType } from 'pages/MerchantPages/Assets/assetTypes';

// actions
import { updateItemMetadata, updateTemplateMetadata, ItemType } from 'ducks/merchantDucks/items/actions';

// components
import StyledContainer from 'components/StyledContainer';
import Input from 'components/Fields/Input';
import AccordionFooter from 'components/AccordionFooter';
import { FullHeightForm, ContentContainer, CenterContent } from 'components/SharedStyledElements';
import { StyledReactIcon } from 'pages/MerchantPages/Audience/components/AudienceDetailsBox';
import Rules from 'restrictions/roles/constants/rules';
import Restricted from 'restrictions/roles/components/Restricted';
import FormAccordionHelper, {
  SectionType,
  PackagesSectionType,
} from 'pages/MerchantPages/Assets/components/AccordionHelper';
import FieldError from 'components/Fields/FieldError';

const { Container, Cell } = Grid;

interface FormValues {
  metadata: Array<MetadataType>;
}

interface OwnProps {
  closePanel: () => void;
  metadata: any;
  itemType: string;
  accordionLabel?: string;
}

type Props = OwnProps;

const transformMetadata = (metadata: Record<string, string>): MetadataType[] => {
  // this is a backend flaw, we save the metadata with 'asset_' prefix
  // to distinguish custom and paywall metadata
  const filterByKey = ([key]: Array<string>) => {
    return key.startsWith('asset_');
  };

  const mapMetadataToArray = ([key, value]: Array<string>) => {
    return { key: key.replace(/asset_/, ''), value, isKeyEditingDisabled: true };
  };

  return Object.entries(metadata).filter(filterByKey).map(mapMetadataToArray);
};

const transformTemplateMetadata = (metadata: Array<any>): MetadataType[] => {
  // this is a backend flaw, we save the metadata with 'asset_' prefix
  // to distinguish custom and paywall metadata
  const filterByKey = (metadataObject: Record<string, any>) => {
    return metadataObject.name.startsWith('asset_');
  };

  const mapMetadataToArray = ({ name, value, id }: Record<string, any>) => {
    return { key: name.replace(/asset_/g, ''), value, id, isKeyEditingDisabled: true };
  };

  return metadata?.filter(filterByKey).map(mapMetadataToArray);
};

const validationSchema = object().shape({
  metadata: array().of(
    object().shape({
      key: string().required('*Required'),
      value: string().required('*Required'),
    })
  ),
});

const MetadataForm: FunctionComponent<Props> = ({
  metadata: initialMetadata,
  closePanel,
  itemType,
  accordionLabel,
}) => {
  const [deletedMetadata, setDeletedMetadata] = useState<MetadataType[]>([]);
  const { hasRule } = useRestrictionRules();
  const isReadOnly = hasRule(Rules.READONLY_ASSETS_AND_PACKAGES);
  const dispatch = useDispatch();

  const { pathname } = useLocation();
  const isEditTemplate = pathname.includes('templates');
  const initialMeta = isEditTemplate ? transformTemplateMetadata(initialMetadata) : transformMetadata(initialMetadata);

  const onFormSubmit = async ({ metadata }: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    const metadataForUpdate: MetadataType[] = [];

    metadata.forEach((metaObject) => {
      const foundMetaObject = initialMeta.find((initialMetaObject) => initialMetaObject.key === metaObject.key);

      if (!foundMetaObject || metaObject.value !== foundMetaObject.value) {
        metadataForUpdate.push(metaObject);
      }
    });

    if (isEditTemplate) {
      await dispatch(updateTemplateMetadata({ metadata: metadataForUpdate, deletedMetadata }));
      setSubmitting(false);
      closePanel();
      return;
    }
    await dispatch(updateItemMetadata({ metadata: metadataForUpdate, deletedMetadata, itemType }));
    setSubmitting(false);
    closePanel();
  };

  return (
    <Formik onSubmit={onFormSubmit} initialValues={{ metadata: initialMeta }} validationSchema={validationSchema}>
      {({ values, isSubmitting, dirty, isValid }: FormikProps<FormValues>) => (
        <FullHeightForm>
          <FormAccordionHelper
            initialValues={{ metadata: initialMeta }}
            index={itemType === ItemType.Package ? PackagesSectionType.Metadata : SectionType.Metadata}
            closePanel={closePanel}
          />
          <ContentContainer>
            <CenterContent>
              <AnalyticsComponent>
                {({ pages, tracker, merchantId, ip }) => (
                  <FieldArray
                    name="metadata"
                    render={(arrayHelpers) => (
                      <Container columns={1}>
                        {values &&
                          values.metadata.map((m: MetadataType, i: number) => {
                            return (
                              <Container columns={7} key={i}>
                                <Cell width={3}>
                                  <Field
                                    tag={m.value ? `text_field_${i}` : 'text_field'}
                                    disabled={m.isKeyEditingDisabled}
                                    name={`metadata[${i}].key`}
                                    type="text"
                                    component={Input}
                                    placeholder="Field"
                                  />
                                  <FieldError name={`metadata[${i}].key`} />
                                </Cell>
                                <Cell width={3}>
                                  <Field
                                    tag={m.value ? `text_value_${i}` : 'text_value'}
                                    name={`metadata[${i}].value`}
                                    disabled={isReadOnly}
                                    type="text"
                                    component={Input}
                                    placeholder="Value"
                                  />
                                  <FieldError name={`metadata[${i}].value`} />
                                </Cell>
                                <Restricted isRestricted={isReadOnly}>
                                  <Cell width={1}>
                                    <StyledReactIcon
                                      as={IoMdTrash}
                                      color={Colors.red}
                                      onClick={() => {
                                        tracker.track({
                                          event: 'click',
                                          type: 'icon',
                                          tag: `button_delete_${i}`,
                                          pages,
                                          merchantId,
                                          ip,
                                        });
                                        setDeletedMetadata([...deletedMetadata, arrayHelpers.form.values.metadata[i]]);
                                        arrayHelpers.remove(i);
                                      }}
                                    />
                                  </Cell>
                                </Restricted>
                              </Container>
                            );
                          })}
                        <StyledContainer columns="minmax(50%, 35rem)" margin="1rem 0 2rem 0">
                          <Cell left={1}>
                            <Button
                              tag="button_add_metadata_field"
                              type="button"
                              onClick={() => arrayHelpers.push({ key: '', value: '' })}
                              buttonModifiers={['buttonLink']}
                              disabled={isReadOnly}
                            >
                              <FaPlus />
                              Add metadata field
                            </Button>
                          </Cell>
                        </StyledContainer>
                      </Container>
                    )}
                  />
                )}
              </AnalyticsComponent>
            </CenterContent>
            <AccordionFooter
              accordionLabel={accordionLabel}
              handleCancel={closePanel}
              disabled={isSubmitting || !dirty || !isValid}
            />
          </ContentContainer>
        </FullHeightForm>
      )}
    </Formik>
  );
};

export default MetadataForm;
